// src/api/types.ts
// Tyypit generoitu OpenAPI-schemasta (openapi.yaml)
// Vastaa backend_scaffold_v3/src/calendar/calendar.dto.ts

export type CalendarEventSource = 'manual' | 'weekly_plan_parser' | 'google_calendar_sync';

export interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  event_date: string;       // "YYYY-MM-DD"
  start_time: string | null; // "HH:MM"
  end_time: string | null;   // "HH:MM"
  all_day: boolean;
  project_id: string | null;
  assigned_to: string | null;
  source: CalendarEventSource;
  created_at?: string;
}

export interface WeeklyPlanParseRequest {
  content: string;
  week_start_date?: string; // "YYYY-MM-DD"
  project_id?: string;
}

export interface WeeklyPlanParseResponse {
  events_created: number;
  week_start: string;
  events: CalendarEvent[];
}

export interface CreateCalendarEventRequest {
  title: string;
  description?: string;
  event_date: string;       // "YYYY-MM-DD"
  start_time?: string;      // "HH:MM"
  end_time?: string;        // "HH:MM"
  project_id?: string;
  assigned_to?: string;
}

export interface CalendarEventsListResponse {
  data: CalendarEvent[];
  total: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponseSuccess {
  accessToken: string;
  user: {
    id: string;
    email: string;
    fullName: string;
    role: string;
    researchAccess?: boolean;
    allowedPages?: string[];
  };
}

/** Käyttäjällä on 2FA enabled — pyydetään Authenticator-koodi. */
export interface LoginResponseRequires2fa {
  requires2fa: true;
  twoFaToken: string;
}

/**
 * C7: käyttäjän rooli vaatii 2FA:n eikä hän ole vielä enrolloinut.
 * Asiakas käyttää enrollmentTokenia /api/auth/2fa/initial-enroll-* -kutsuihin.
 */
export interface LoginResponseRequiresEnrollment {
  requiresEnrollment: true;
  enrollmentToken: string;
}

export type LoginResponse =
  | LoginResponseSuccess
  | LoginResponseRequires2fa
  | LoginResponseRequiresEnrollment;

/** Apufunktio joka kaventaa LoginResponse-tyypin */
export function isLoginSuccess(r: LoginResponse): r is LoginResponseSuccess {
  return 'accessToken' in r;
}
export function isLoginRequires2fa(r: LoginResponse): r is LoginResponseRequires2fa {
  return 'requires2fa' in r && r.requires2fa === true;
}
export function isLoginRequiresEnrollment(r: LoginResponse): r is LoginResponseRequiresEnrollment {
  return 'requiresEnrollment' in r && r.requiresEnrollment === true;
}

// ── 2FA / TOTP ────────────────────────────────────────────────

export interface TwoFactorStatusResponse {
  enabled: boolean;
  enrolledAt: string | null;
  backupCodesRemaining: number;
}

export interface EnrollInitResponse {
  /** Base32-koodattu TOTP-secret (manuaalista syöttöä varten) */
  secret: string;
  /** otpauth://... URL */
  otpauthUrl: string;
  /** QR-koodin data URL (data:image/png;base64,...) */
  qrCodeDataUrl: string;
}

export interface EnrollConfirmResponse {
  enrolled: true;
  enrolledAt: string;
  /** 10 backup-koodia plain-text muodossa — näytetään KERRAN. */
  backupCodes: string[];
}

export interface ApiError {
  statusCode: number;
  message: string;
  errors?: string[];
}

// ── C4 Login events ──────────────────────────────────────────

export interface LoginEvent {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  success: boolean;
  isNewIp: boolean;
  isNewUserAgent: boolean;
  createdAt: string;
}

// ── C6 Password rotation ─────────────────────────────────────

export interface PasswordRotationStatus {
  daysSinceChanged: number | null;
  isDue: boolean;
  dueAt: string | null;
  daysUntilDue: number | null;
}

// ── D4 Sub-processors ────────────────────────────────────────

export interface SubProcessor {
  id: string;
  name: string;
  serviceRole: string;
  countryCode: string;
  dataCategories: string[];
  dpaSigned: boolean;
  dpaUrl: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SubProcessorInput {
  name: string;
  serviceRole: string;
  countryCode: string;
  dataCategories: string[];
  dpaSigned?: boolean;
  dpaUrl?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

// ── D9 Material compliance ───────────────────────────────────

export interface MaterialComplianceStatus {
  reach: 'ok' | 'warning' | 'missing';
  rohs: 'ok' | 'non_compliant' | 'unknown';
  msds: 'valid' | 'expiring' | 'expired' | 'missing';
  overallRisk: 'low' | 'medium' | 'high' | 'critical';
  reasons: string[];
}

export interface MaterialComplianceReport {
  materialId: string;
  sku: string;
  name: string;
  casNumber: string | null;
  reachRegistrationNumber: string | null;
  rohsCompliant: boolean | null;
  svhcSubstance: boolean;
  hazardStatements: string[];
  precautionaryStatements: string[];
  latestMsdsDocumentId: string | null;
  latestMsdsValidFrom: string | null;
  latestMsdsValidUntil: string | null;
  status: MaterialComplianceStatus;
}

// ── D13 Material documents ───────────────────────────────────

export interface MaterialDocument {
  id: string;
  originalName: string;
  documentCategory: string | null;
  validFrom: string | null;
  validUntil: string | null;
  title: string | null;
  description: string | null;
  mimeType: string;
  fileSizeBytes: number;
  createdAt: string;
  uploadedBy: { id: string; fullName: string; email: string };
}
